/**
 * PartialYearBanner - Enhanced UX for partial-year handling
 * 
 * Shows a visual timeline of pre-event vs post-event months,
 * with clear explanation of how data is handled during interruption.
 */

import React from 'react';
import { AlertTriangle, Calendar, TrendingDown, CheckCircle, Info, Zap } from 'lucide-react';

interface PartialYearInfo {
  coverage_year: number;
  original_months: number;
  padded_months: number;
  event_start_month: number;
  event_date?: string;
}

interface PartialYearBannerProps {
  partialYearInfo: PartialYearInfo;
  onLearnMore?: () => void;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function PartialYearBanner({ partialYearInfo, onLearnMore }: PartialYearBannerProps) {
  const { coverage_year, original_months, padded_months, event_start_month } = partialYearInfo;
  
  // Calculate which months are pre-event vs post-event
  const eventMonthIndex = event_start_month - 1; // 0-indexed
  
  // Generate timeline data
  const timelineMonths = MONTH_NAMES.map((name, idx) => {
    const isPreEvent = idx < eventMonthIndex;
    const isEventMonth = idx === eventMonthIndex;
    const isPostEvent = idx > eventMonthIndex;
    
    return {
      name,
      monthNum: idx + 1,
      isPreEvent,
      isEventMonth,
      isPostEvent,
      hasData: isPreEvent || isEventMonth,
    };
  });
  
  const preEventMonths = timelineMonths.filter(m => m.isPreEvent).length;
  const postEventMonths = timelineMonths.filter(m => m.isPostEvent).length;

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
      {/* Header */}
      <div className="px-4 py-3" style={{ background: 'var(--color-needs-human-tint)', borderBottom: '1px solid var(--color-needs-human-tint-bd)' }}>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
          <h4 className="cs-body" style={{ color: 'var(--color-needs-human-text)', fontWeight: 600 }}>Mid-Year Business Interruption Detected</h4>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Timeline Visualization */}
        <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
            <span className="cs-body" style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>{coverage_year} Monthly Timeline</span>
          </div>
          
          {/* Visual Timeline */}
          <div className="flex gap-1">
            {timelineMonths.map((month, idx) => (
              <div
                key={month.name}
                className={`flex-1 relative group`}
              >
                {/* Month Bar */}
                <div
                  className="h-8 rounded-sm flex items-center justify-center text-xs font-medium transition-all"
                  style={
                    month.isPreEvent
                      ? { background: 'var(--color-verified)', color: 'var(--color-on-brand)' }
                      : month.isEventMonth
                      ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)', boxShadow: '0 0 0 2px var(--color-brand-hover), 0 0 0 3px var(--color-bg-surface)' }
                      : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)' }
                  }
                >
                  {month.name}
                </div>

                {/* Tooltip on hover */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  <div
                    className="px-2 py-1 rounded text-xs whitespace-nowrap"
                    style={
                      month.isPreEvent
                        ? { background: 'var(--color-verified)', color: 'var(--color-on-brand)' }
                        : month.isEventMonth
                        ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
                        : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                    }
                  >
                    {month.isPreEvent && 'Pre-event data'}
                    {month.isEventMonth && (
                      <span className="inline-flex items-center gap-1">
                        <Zap className="w-3 h-3" /> Event started
                      </span>
                    )}
                    {month.isPostEvent && 'Set to $0'}
                  </div>
                </div>

                {/* Event marker */}
                {month.isEventMonth && (
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2">
                    <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: 'var(--color-brand)' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--color-verified)' }} />
              <span style={{ color: 'var(--color-text-muted)' }}>Pre-event ({preEventMonths} months)</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--color-brand)' }} />
              <span style={{ color: 'var(--color-text-muted)' }}>Event start</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm" style={{ background: 'var(--color-bg-elevated)' }} />
              <span style={{ color: 'var(--color-text-muted)' }}>Post-event ($0)</span>
            </div>
          </div>
        </div>
        
        {/* Explanation Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* What We're Using */}
          <div className="rounded-lg p-3" style={{ background: 'transparent', border: '1px solid var(--color-verified)' }}>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
              <span className="cs-body" style={{ color: 'var(--color-verified)', fontWeight: 500 }}>Data Used</span>
            </div>
            <ul className="cs-body space-y-1" style={{ color: 'var(--color-verified)' }}>
              <li>• {original_months} months of actual pre-event data</li>
              <li>• Revenue & expenses through {MONTH_NAMES[eventMonthIndex - 1] || MONTH_NAMES[0]}</li>
              <li>• Historical patterns for projection</li>
            </ul>
          </div>

          {/* What We're Assuming */}
          <div className="rounded-lg p-3" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
              <span className="cs-body" style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>Interruption Period</span>
            </div>
            <ul className="cs-body space-y-1" style={{ color: 'var(--color-text-muted)' }}>
              <li>• {padded_months} months set to $0 revenue</li>
              <li>• {MONTH_NAMES[eventMonthIndex]} through Dec assumed closed</li>
              <li>• This represents business interruption loss</li>
            </ul>
          </div>
        </div>
        
        {/* Info Note */}
        <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}>
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-text-muted)' }} />
          <div className="cs-body" style={{ color: 'var(--color-text-secondary)' }}>
            <span style={{ fontWeight: 500 }}>How this affects your claim:</span>
            <span className="ml-1" style={{ color: 'var(--color-text-muted)' }}>
              The BI calculation compares your pre-event performance (benchmark) against the
              interruption period. Setting post-event months to $0 captures the full loss during closure.
              {postEventMonths >= 6 && (
                <span className="flex items-start gap-1 mt-1" style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> Note: {postEventMonths} months of zero revenue is significant. Ensure your event dates are accurate.
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Optional Learn More */}
        {onLearnMore && (
          <button
            onClick={onLearnMore}
            className="cs-body underline"
            style={{ color: 'var(--color-needs-human-text)', fontWeight: 500 }}
          >
            Learn more about partial-year calculations →
          </button>
        )}
      </div>
    </div>
  );
}
